import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, MapPin, Skull, Flag, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { MiniMap } from "@/components/MiniMap";
import { BattleOfTheDay } from "@/components/BattleOfTheDay";

const yearLabel = (y) => (y == null ? "" : y < 0 ? `${Math.abs(y)} BC` : String(y));

export const warPhrase = (w) => (/^(the|a|an)\s/i.test(w) || /^(World War|Operation |Battle of|Siege of)/.test(w) ? w.replace(/^The\s/, "the ") : `the ${w}`);

export const BattleCard = ({ b, testid }) => (
  <article data-testid={testid} className="flex flex-col bg-white border border-[#e5e7eb] rounded-xl p-4">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[22px] font-bold text-gold leading-none">{yearLabel(b.year)}</span>
      <span className="px-2 py-0.5 rounded-full bg-ink text-gold text-[10px] font-bold uppercase tracking-wider">{b.era}</span>
    </div>
    <h3 className="mt-2 text-[16px] font-bold text-ink leading-snug">{b.battle}</h3>
    {b.belligerents && <p className="mt-1 text-[13px] text-ink flex items-start gap-1.5"><Flag className="w-3.5 h-3.5 mt-0.5 text-ink-muted shrink-0" /> {b.belligerents}</p>}
    {b.war && <p className="mt-1 text-[12px] text-ink-muted">Part of {warPhrase(b.war)}</p>}
    {b.result && <p className="mt-1 text-[13px] text-ink-muted">{b.result}</p>}
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-muted">
      {b.casualties && <span className="inline-flex items-center gap-1"><Skull className="w-3.5 h-3.5" /> {b.casualties}</span>}
      {b.location && (b.map_link ? <a href={b.map_link} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-map`} className="inline-flex items-center gap-1 font-semibold text-gold hover:underline"><MapPin className="w-3.5 h-3.5" /> {b.location}</a> : <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {b.location}</span>)}
    </div>
    {b.notable && <p className="mt-2 text-[12px] italic text-ink-muted border-t border-[#f1f5f9] pt-2">{b.notable}</p>}
    {b.wiki_url && <a href={b.wiki_url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-wiki`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-ink-muted hover:text-gold"><ExternalLink className="w-3 h-3" /> Wikipedia</a>}
    {b.lat != null && <div className="mt-3"><MiniMap lat={b.lat} lon={b.lon} label={b.location || b.region} href={b.map_link} testid={`${testid}-minimap`} /></div>}
  </article>
);

export const BattlesSection = ({ ddmm, dateLabel = "today" }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!ddmm) return;
    api.get("/battles", { params: { date: ddmm, limit: 12 } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [] }));
  }, [ddmm]);
  if (!data || data.total === 0) return null;
  return (
    <section id="battles" data-testid="battles-section" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Swords className="w-5 h-5 text-gold" />
          <h2 className="text-2xl font-bold text-ink">Historic Battles</h2>
          <span data-testid="battles-count" className="ml-1 text-[12px] font-semibold text-ink-muted">{data.total}</span>
        </div>
        <p className="text-[13px] text-ink-muted">Fought on {dateLabel}. <Link to="/battles" data-testid="battles-all-link" className="font-semibold text-gold hover:underline">All battles →</Link></p>
      </div>
      <BattleOfTheDay ddmm={ddmm} dateLabel={dateLabel} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="battles-grid">
        {data.items.map((b, i) => <BattleCard key={b.id} b={b} testid={`battle-card-${i + 1}`} />)}
      </div>
    </section>
  );
};
